// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { getSessionUser } from '@/lib/authSession';

export const useScoutAthleteNotes = (athleteId?: string) => {
  const { profile } = useAuth();
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (athleteId && profile?.id) {
      fetchNotes();
    }
  }, [athleteId, profile?.id]);

  const fetchNotes = async () => {
    if (!athleteId || !profile?.id) return;

    try {
      setIsLoading(true);
      
      // Debug logging for note loading
      const { data: userData } = await getSessionUser();
      console.log("👤 Loading note for scout UID:", userData?.user?.id);
      console.log("📦 Loading note for athlete ID:", athleteId);

      const { data, error } = await supabase
        .from('scout_athlete_notes')
        .select('notes')
        .eq('scout_id', userData?.user?.id)
        .eq('athlete_id', athleteId)
        .maybeSingle();

      if (error) {
        console.error("❌ Failed to load note:", error);
        throw error;
      } else {
        console.log("📥 Loaded note from DB:", data?.notes);
      }
      
      setNotes(data?.notes || '');
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch notes');
    } finally {
      setIsLoading(false);
    }
  };

  const saveNotes = async (noteContent: string) => {
    if (!athleteId || !profile?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      // Debug logging
      const { data: userData } = await getSessionUser();
      console.log("🔍 Logged-in scout UID:", userData?.user?.id);
      const scoutId = userData?.user?.id;
      console.log("🔍 Scout ID being sent in payload:", scoutId);

      try {
        const { error } = await supabase
          .from('scout_athlete_notes')
          .upsert({
            scout_id: scoutId,
            athlete_id: athleteId,
            notes: noteContent,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'scout_id,athlete_id'
          });

        if (error) {
          console.error("❌ Error saving note:", error);
          throw error;
        } else {
          console.log("✅ Note saved successfully");
        }
      } catch (err) {
        console.error("💥 Exception during note save:", err);
        throw err;
      }
      
      setNotes(noteContent);
    } catch (err) {
      console.error('Error saving notes:', err);
      setError(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    notes,
    saveNotes,
    isLoading,
    error,
    refetch: fetchNotes
  };
};
